import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload, Save, ImageOff } from "lucide-react";
import { getSettings, DEFAULT_HERO_BANNER } from "@/lib/api";
import { adminSaveSettings, adminUploadImage, resolveImage } from "@/lib/adminApi";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-sm font-bold text-navy mb-1.5">{label}</span>
    {children}
  </label>
);

export const SiteSettingsForm = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (data && !form) setForm(data); }, [data, form]);

  if (!form) return <Loader2 className="h-6 w-6 animate-spin text-eco" />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUploadImage(file);
      setForm((f) => ({ ...f, hero_banner_image: res.url }));
      toast.success("Imagine încărcată. Apasă Salvează.");
    } catch {
      toast.error("Eroare la încărcarea imaginii");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await adminSaveSettings(form);
      qc.setQueryData(["settings"], saved);
      toast.success("Setări salvate");
    } catch {
      toast.error("Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const preview = form.hero_banner_image ? resolveImage(form.hero_banner_image) : DEFAULT_HERO_BANNER;

  return (
    <div className="space-y-6" data-testid="site-settings-form">
      <div className="bg-white border-2 border-slate-100 rounded-xl p-5 space-y-4">
        <h3 className="font-display font-black text-navy">Banner principal</h3>
        <img src={preview} alt="Banner" className="w-full max-h-72 object-contain bg-navy rounded-lg" data-testid="settings-banner-preview" />
        {!form.hero_banner_image && <p className="text-xs text-muted-foreground">Se afișează bannerul actual (implicit).</p>}
        <div className="flex flex-wrap gap-2">
          <label className="bg-navy hover:bg-navy-700 text-white text-sm font-bold px-4 py-2.5 rounded-xl inline-flex items-center gap-2 cursor-pointer" data-testid="settings-banner-upload-label">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Încarcă banner nou
            <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} data-testid="settings-banner-upload-input" />
          </label>
          {form.hero_banner_image && (
            <button onClick={() => setForm({ ...form, hero_banner_image: "" })} className="bg-slate-100 hover:bg-slate-200 text-navy text-sm font-bold px-4 py-2.5 rounded-xl inline-flex items-center gap-2" data-testid="settings-banner-reset">
              <ImageOff className="h-4 w-4" /> Revino la bannerul implicit
            </button>
          )}
        </div>
        <Field label="Titlu banner (lasă gol ca să nu apară)">
          <input className="inp" value={form.hero_title} onChange={set("hero_title")} data-testid="settings-hero-title" />
        </Field>
        <Field label="Descriere banner">
          <textarea className="inp min-h-[80px]" value={form.hero_subtitle} onChange={set("hero_subtitle")} data-testid="settings-hero-subtitle" />
        </Field>
      </div>

      <div className="bg-white border-2 border-slate-100 rounded-xl p-5 space-y-4">
        <h3 className="font-display font-black text-navy">Pagina Produse</h3>
        <Field label="Titlu">
          <input className="inp" value={form.products_title} onChange={set("products_title")} data-testid="settings-products-title" />
        </Field>
        <Field label="Subtitlu">
          <textarea className="inp min-h-[80px]" value={form.products_subtitle} onChange={set("products_subtitle")} data-testid="settings-products-subtitle" />
        </Field>
      </div>

      <button onClick={save} disabled={saving || uploading} className="bg-eco hover:bg-green-700 disabled:opacity-60 text-white font-display font-black px-6 py-3 rounded-xl inline-flex items-center gap-2" data-testid="settings-save-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} SALVEAZĂ
      </button>
    </div>
  );
};
